import React, { useState, useEffect } from 'react';
import { VStack, HStack, Text, Button, NativeSelect } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useGetProfileConfigQuery, useUpdateProfileConfigMutation, useGetUserByIdQuery } from '../api';
import LoadingSkeleton from '@/components/LoadingSkeleton';

interface EditConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const EditConfigurationModal: React.FC<EditConfigurationModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [showConditionGroups, setShowConditionGroups] = useState<boolean | null>(null);
  const [originalShowConditionGroups, setOriginalShowConditionGroups] = useState<boolean | null>(null);
  const [showConditionSearch, setShowConditionSearch] = useState<boolean | null>(null);
  const [originalShowConditionSearch, setOriginalShowConditionSearch] = useState<boolean | null>(null);
  const [redirectSpeedbumpToConditionPage, setRedirectSpeedbumpToConditionPage] = useState<boolean | null>(null);
  const [originalRedirectSpeedbumpToConditionPage, setOriginalRedirectSpeedbumpToConditionPage] = useState<boolean | null>(null);
  const [showAssistantDoctorPicker, setShowAssistantDoctorPicker] = useState<boolean | null>(null);
  const [originalShowAssistantDoctorPicker, setOriginalShowAssistantDoctorPicker] = useState<boolean | null>(null);
  const [showEnrolments, setShowEnrolments] = useState<boolean | null>(null);
  const [enableDashboardChat, setEnableDashboardChat] = useState<boolean | null>(null);
  const [originalShowEnrolments, setOriginalShowEnrolments] = useState<boolean | null>(null);
  const [originalEnableDashboardChat, setOriginalEnableDashboardChat] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });

  // Fetch profile config when modal opens and user is selected
  const { data: profileConfig, isFetching: isLoadingConfig } = useGetProfileConfigQuery(user?.id || 0, {
    skip: !user?.id || !isOpen,
    refetchOnMountOrArgChange: true,
  });

  const [updateProfileConfig, { isLoading: isUpdating }] = useUpdateProfileConfigMutation();

  // Initialize form when profile config loads
  useEffect(() => {
    if (profileConfig) {
      const initialConditionGroups = profileConfig.show_condition_groups ?? null;
      const initialConditionSearch = profileConfig.show_condition_search ?? null;
      const initialRedirectSpeedbumpToConditionPage = profileConfig.redirect_speedbump_to_condition_page ?? null;
      const initialAssistantDoctorPicker = profileConfig.show_assistant_doctor_picker ?? null;
      const initialShowEnrolments = profileConfig.show_enrolments ?? null;
      const initialEnableDashboardChat = profileConfig.enable_dashboard_chat ?? null;
      setShowConditionGroups(initialConditionGroups);
      setOriginalShowConditionGroups(initialConditionGroups);
      setShowConditionSearch(initialConditionSearch);
      setOriginalShowConditionSearch(initialConditionSearch);
      setRedirectSpeedbumpToConditionPage(initialRedirectSpeedbumpToConditionPage);
      setOriginalRedirectSpeedbumpToConditionPage(initialRedirectSpeedbumpToConditionPage);
      setShowAssistantDoctorPicker(initialAssistantDoctorPicker);
      setOriginalShowAssistantDoctorPicker(initialAssistantDoctorPicker);
      setShowEnrolments(initialShowEnrolments);
      setEnableDashboardChat(initialEnableDashboardChat);
      setOriginalShowEnrolments(initialShowEnrolments);
      setOriginalEnableDashboardChat(initialEnableDashboardChat);
    }
  }, [profileConfig]);

  const handleSubmit = async () => {
    if (!user) return;

    setError('');

    // Check if anything changed
    const hasChanges =
      showConditionGroups !== originalShowConditionGroups ||
      showConditionSearch !== originalShowConditionSearch ||
      redirectSpeedbumpToConditionPage !== originalRedirectSpeedbumpToConditionPage ||
      showAssistantDoctorPicker !== originalShowAssistantDoctorPicker ||
      enableDashboardChat !== originalEnableDashboardChat ||
      showEnrolments !== originalShowEnrolments;

    // If nothing changed, just close
    if (!hasChanges) {
      onClose();
      return;
    }

    // Build update request with only changed fields
    const updateRequest: { id: number } & Record<string, number | boolean | null> = {
      id: user.id,
    };

    if (showConditionGroups !== originalShowConditionGroups) {
      updateRequest.show_condition_groups = showConditionGroups;
    }
    if (showConditionSearch !== originalShowConditionSearch) {
      updateRequest.show_condition_search = showConditionSearch;
    }
    if (redirectSpeedbumpToConditionPage !== originalRedirectSpeedbumpToConditionPage) {
      updateRequest.redirect_speedbump_to_condition_page = redirectSpeedbumpToConditionPage;
    }
    if (showAssistantDoctorPicker !== originalShowAssistantDoctorPicker) {
      updateRequest.show_assistant_doctor_picker = showAssistantDoctorPicker;
    }
    if (showEnrolments !== originalShowEnrolments) {
      updateRequest.show_enrolments = showEnrolments;
    }
    if (enableDashboardChat !== originalEnableDashboardChat) {
      updateRequest.enable_dashboard_chat = enableDashboardChat;
    }

    try {
      await updateProfileConfig(updateRequest).unwrap();

      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to update profile configuration');
    }
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const isLoading = isLoadingConfig || isUpdating;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change Settings</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>

      <DrawerBody>
        {isLoadingConfig ? (
          <LoadingSkeleton rows={2} />
        ) : (
          <VStack gap={4} align="stretch">
            {user && (
              <Text fontSize="sm" color="gray.600">
                Editing profile settings for: <strong>{user.first_name} {user.last_name}</strong>
              </Text>
            )}

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Condition Groups
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show condition groups"
                  value={showConditionGroups === null ? 'inherit' : String(showConditionGroups)}
                  onChange={(event) => setShowConditionGroups(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether condition groups are displayed in the user's profile
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Condition Search
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show condition search"
                  value={showConditionSearch === null ? 'inherit' : String(showConditionSearch)}
                  onChange={(event) => setShowConditionSearch(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether condition group search is displayed in the user's profile
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Route Patients Directly to Condition Page
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Redirect speedbump to condition page"
                  value={redirectSpeedbumpToConditionPage === null ? 'inherit' : String(redirectSpeedbumpToConditionPage)}
                  onChange={(event) => setRedirectSpeedbumpToConditionPage(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Removes the intermediate selection screen for a faster, one-tap experience. Disable to keep the privacy buffer where patients self-select their condition.
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Assistant Doctor Picker
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show assistant doctor picker"
                  value={showAssistantDoctorPicker === null ? 'inherit' : String(showAssistantDoctorPicker)}
                  onChange={(event) => setShowAssistantDoctorPicker(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether assistants can switch doctors in the send drawer
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Show Enrollments
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Show enrolments"
                  value={showEnrolments === null ? 'inherit' : String(showEnrolments)}
                  onChange={(event) => setShowEnrolments(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Controls whether this organization's users can access enrollments
              </Text>
            </div>

            <div>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Enable Dashboard Chat
              </Text>
              <NativeSelect.Root>
                <NativeSelect.Field
                  aria-label="Enable dashboard chat"
                  value={enableDashboardChat === null ? 'inherit' : String(enableDashboardChat)}
                  onChange={(event) => setEnableDashboardChat(event.target.value === 'inherit' ? null : event.target.value === 'true')}
                >
                  <option value="inherit">Inherit from organization</option>
                  <option value="true">Enabled</option>
                  <option value="false">Disabled</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Enables AI chat for this doctor context, including assigned assistants. The global chat switch must also be enabled.
              </Text>
            </div>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </VStack>
        )}
      </DrawerBody>

      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isUpdating}
            disabled={isLoading}
            colorPalette="cyan"
          >
            Save Configuration
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(EditConfigurationModal, {
  action: 'edit-configuration',
});
